import React, {
	createContext,
	createElement,
	CSSProperties,
	Fragment,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
	bookmarkLevel,
	createLevelComment,
	deleteLevel,
	favouriteLevel,
	featureLevel,
	getLevel,
	getLevelComment,
	getLevelCommentsPage,
	getLevelData,
	getLevelsList,
	getUserSet,
	getUsersWithAccess,
	manageLevelAccess,
	obliterateLevel,
	privateLevel,
	rateLevel,
	removeLevelRate,
	restoreLevel,
	unfeatureLevel,
	updateLevelVideo,
} from "../../../api";
import {
	Button,
	Modal,
	OverlayTrigger,
	Spinner,
	Tooltip,
} from "react-bootstrap";
import { GlobalContext, UserContext } from "../../../App";
import YouTubeIframeLoader from "youtube-iframe";

import DefaultThumbnail from "../../../assets/images/DefaultThumbnail.png";
import downloadFile from "../../../functions/downloadFile";
import { Id, toast } from "react-toastify";
import pushHistory from "../../../functions/pushHistory";
import { LevelConstants, UserConstants } from "../../../constants";
import { mapHighestRole } from "../../../functions/styleAppliers";
import MapStars from "../../Insertions/MapStars";
import ActionButton from "../../Insertions/ActionButton";
import moment from "moment";
import {
	getActiveStats,
	ImageStarContainer,
} from "../LevelDisplayTypes/LevelDisplayTypes";
import Paginate from "../../Pagination/Pagination";
import UsernameAvatarDisplay from "../../Insertions/UsernameAvatarDisplay";
import ReadMore from "../../Insertions/ReadMore";
import { CommentSort } from "./types";
import ToggleSwitch from "../../Insertions/ToggleSwitch";
import CommentButtons from "../../Insertions/CommentButtons";
import removeExcessiveBreaks from "../../../functions/removeExcessiveBreaks";
import CommentReaction from "../../Insertions/CommentReaction";

import "./style.scss";

const LevelContext = createContext<any>(null);
const useQuery = () => {
	return new URLSearchParams(useLocation().search);
};

const Level = (props) => {
	const levelId = props?.match?.params?.id;
	const { gameProperties } = useContext(GlobalContext);
	const { currentUserData } = useContext(UserContext);
	const query = useQuery();

	const [hasDoneQuery, setHasDoneQuery] = useState<boolean>(false);
	const [noReveal, setNoReveal] = useState<number>(1); // 1 = big, 0 = normal, 2 = small

	const [level, setLevel] = useState<any>(null);
	const [levelMessage, setLevelMessage] = useState<string>("");
	const [focusImage, setFocusImage] = useState<boolean>(false);
	const [modalSize, setModalSize] = useState<undefined | "lg" | "xl" | "sm">(
		undefined,
	);

	const [currentModal, setCurrentModal] = useState<number>(0);
	const [randomLevels, setRandomLevels] = useState<any>(null);
	const [usersWithAccess, setUsersWithAccess] = useState<any[]>([]);

	const [voteAuthors, setVoteAuthors] = useState<object[]>([]);
	const [ratesToMap, setRatesToMap] = useState<number[]>([]);
	const [numberOfVotePages, setNumberOfVotePages] = useState<number>(0);
	const [ratepage, setRatepage] = useState<number>(1);

	const votesRaterProps = {
		voteAuthors,
		ratesToMap,
		setRatesToMap,
		setVoteAuthors,
	};

	const userId = currentUserData?._id;
	const isAuthor =
		level?.otherAuthors?.includes(userId) || level?.author === userId;
	const cacheBust: boolean = currentUserData?.staffSession || isAuthor;
	const cacheBustStr: string = cacheBust
		? `?v=${level?.lastEditedAny || Date.now()}`
		: "";
	// reset modal size
	useEffect(() => {
		if (currentModal === 0) setModalSize(undefined);
	}, [currentModal]);

	useEffect(() => {
		// reset states when levelid updates
		setVoteAuthors([]);
		setRatesToMap([]);
		setUsersWithAccess([]);
	}, [level?._id]);

	const game = gameProperties?.find(
		(game) => game?.internalID === level?.game,
	);

	// rolling more random levels
	const handleRollMore = useCallback(
		(e) => {
			e.preventDefault();
			setNoReveal(2);
			setHasDoneQuery(false);
			const msg = toast.loading("Let's go gambling!");
			let requestType = "get";
			if (query.get("favourited") === "1") requestType = "favourites";
			if (query.get("bookmarked") === "1") requestType = "bookmarks";
			if (query.get("featured") === "1") requestType = "featured";
			query.set("count", "6");
			query.set("authors", "1");
			getLevelsList(requestType, query.toString())
				.then((res) => {
					setModalSize("xl");
					setRandomLevels(res?.data?.levels);
					toast.success("I can't stop winning!");
					setCurrentModal(2);
					requestAnimationFrame(() =>
						requestAnimationFrame(() => setNoReveal(1)),
					);
				})
				.catch(() => {
					toast.error("Awh dang it (No levels found for your query)");
					setNoReveal(0);
				})
				.finally(() => {
					toast.dismiss(msg);
					setHasDoneQuery(true);
				});
		},
		[query],
	);

	// fallback if the user closes the modal without picking a level
	useEffect(() => {
		if (hasDoneQuery && currentModal === 0 && noReveal === 2) {
			setTimeout(() => requestAnimationFrame(() => setNoReveal(0)), 800);
		}
	}, [hasDoneQuery, currentModal, noReveal]);

	const fetchLevelLogic = useCallback((id) => {
		document.title = `Loading level... | Level Share Square`;
		getLevel({ id })
			.then((response) => {
				if (response?.data) {
					const levelRes = response?.data?.level;
					setLevel(levelRes);
					setLevelMessage(response?.data?.message);
					document.title = `${levelRes?.name} by ${levelRes?.author?.username} | Level Share Square`;
				}
			})
			.catch((err) => {
				document.title = `No level found | Level Share Square`;
				setLevelMessage(err?.response?.data?.message);
			})
			.finally(() => {
				setHasDoneQuery(true);
				requestAnimationFrame(() => setNoReveal(0));
			});
	}, []);

	useEffect(() => {
		if (!level && !hasDoneQuery) {
			// fetch level
			fetchLevelLogic(levelId);
		}
	}, [level, hasDoneQuery, fetchLevelLogic, levelId]);

	const modalObj = {
		0: (
			<span className="modal-fill">
				<Modal.Body className="modal-bdy modal-filter">
					<h1 className=" center-textoutput">Closing...</h1>
				</Modal.Body>
			</span>
		),
		1: (
			<AccessManageModal
				usersWithAccess={usersWithAccess}
				setUsersWithAccess={setUsersWithAccess}
			/>
		),
		2: (
			<RandomLevelModal
				levels={randomLevels}
				setLevel={setLevel}
				setCurrentModal={setCurrentModal}
				setNoReveal={setNoReveal}
				fetchLevelLogic={fetchLevelLogic}
			/>
		),
		3: (
			<UserRatesModal
				{...votesRaterProps}
				numberOfVotePages={numberOfVotePages}
				setNumberOfVotePages={setNumberOfVotePages}
				ratepage={ratepage}
				setRatepage={setRatepage}
			/>
		),
		4: <FeatureModal />,
		5: <VideoModal />,
	};

	// loading message
	if (!level && !hasDoneQuery)
		return (
			<div className="d-flex justify-content-center">
				<Spinner animation="grow" variant="primary" />
			</div>
		);

	// level not found
	if (!level)
		return (
			<div className="container mt-4">
				<div className="card">
					<div className="card-body">
						<h1 className="center-textoutput">
							{levelMessage || "Level not found"}
						</h1>
					</div>
				</div>
			</div>
		);

	// if there is a level and page
	return (
		<LevelContext.Provider
			value={{ level, setLevel, game, setCurrentModal, setModalSize }}>
			<MainPage
				setFocusImage={setFocusImage}
				handleRollMore={handleRollMore}
				noReveal={noReveal}
				cacheBustStr={cacheBustStr}
			/>
			{focusImage && (
				<div
					onClick={() => setFocusImage((p) => !p)}
					className="fullscreen-overlay">
					<img
						src={
							level?.thumbnail + cacheBustStr || DefaultThumbnail
						}
						alt="Level Thumbnail"
						onError={(
							e: React.SyntheticEvent<HTMLImageElement, Event>,
						) => {
							const target = e.currentTarget as HTMLImageElement;
							target.src = DefaultThumbnail;
						}}
					/>
				</div>
			)}
			<Modal
				show={currentModal > 0}
				onHide={() => {
					if (currentModal === 2) setNoReveal(2);
					setCurrentModal(0);
				}}
				size={modalSize}
				className="popup-modal"
				aria-labelledby="contained-modal-title-vcenter"
				centered>
				{modalObj?.[currentModal]}
			</Modal>
		</LevelContext.Provider>
	);
};

const MainPage = ({
	setFocusImage,
	handleRollMore,
	noReveal,
	cacheBustStr,
}) => {
	const pageRef = useRef(null);
	const buttonProps = {};
	const { level } = useContext(LevelContext);

	const [scrollButtonRotation, setScrollButtonRotation] = useState(0);
	const commentTabsRef = useRef<null | HTMLDivElement>(null);

	useEffect(() => {
		const onScroll = () => {
			const el = commentTabsRef.current;
			if (!el) return;

			const rect = el.getBoundingClientRect();
			// Point up if it's below viewport, down if above
			if (rect.top < 165) {
				setScrollButtonRotation(
					(prev) => prev + (prev % 2 === 0 ? 1 : 0),
				); // will scroll up
			} else if (rect.bottom + 100 > window.innerHeight) {
				setScrollButtonRotation(
					(prev) => prev + (prev % 2 !== 0 ? 1 : 0),
				); // will scroll down
			}
		};

		window.addEventListener("scroll", onScroll);
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	const handleScroll = useCallback(() => {
		const el = commentTabsRef.current;
		if (!el) return;

		const rect = el.getBoundingClientRect();

		if (scrollButtonRotation % 2 !== 0) {
			window.scrollTo({ top: 0, behavior: "smooth" });
		} else {
			window.scrollTo({
				top: rect.top + window.scrollY - 100,
				behavior: "smooth",
			});
		}
	}, [scrollButtonRotation]);

	useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.key === "Escape") {
				handleScroll();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [handleScroll]);

	return (
		<>
			<div
				className="container level-mainpage"
				style={{
					transform:
						noReveal === 0
							? "scale(1)"
							: noReveal === 1
								? "scale(0.5)"
								: "scale(1.5)",
					opacity: noReveal === 0 ? 1 : 0,
					transition: "all 1s ease-in-out",
					pointerEvents: !noReveal ? "auto" : "none",
					transformOrigin: "top center",
				}}>
				<div className="action-flex no-blur">
					<ButtonActions
						{...buttonProps}
						handleRollMore={handleRollMore}
					/>
				</div>
				<div className="card" ref={pageRef}>
					<LevelHeader />
					<div className="card-body">
						<LevelBody
							setFocusImage={setFocusImage}
							cacheBustStr={cacheBustStr}
						/>
						<LevelMiddleBody
							// attach a key to refresh the rates when the level changes
							key={level?._id + "-middle-body"}
						/>
					</div>
				</div>
				<LevelFooter commentTabsRef={commentTabsRef} />
			</div>
			<button
				className="level-bottom-seeker unselectable material-icons"
				style={{
					transform: `rotate(${scrollButtonRotation * 180}deg)`,
				}}
				onClick={handleScroll}>
				arrow_circle_down
			</button>
		</>
	);
};

const LevelHeader = () => {
	const { level, game } = useContext(LevelContext);

	return (
		<div
			className="card-header"
			style={{
				display: "flex",
				flexDirection: "row",
				flexWrap: "nowrap",
				justifyContent: "flex-start",
				alignItems: "center",
				gap: "10px",
			}}>
			<OverlayTrigger
				overlay={<Tooltip id="game">{game?.name}</Tooltip>}
				placement="top">
				<img
					alt="Game Logo"
					src={game?.logo}
					style={{
						borderRadius: "50%",
						borderWidth: "3px",
						borderStyle: "solid",
						borderColor: "white",
					}}
					width="50"
					height="50"
				/>
			</OverlayTrigger>
			<OverlayTrigger
				overlay={
					<Tooltip id="levelname">
						<div className="level-page-tooltip">{level?.name}</div>
					</Tooltip>
				}
				placement="top">
				<h1
					style={{
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
						margin: "0",
						padding: "0 auto",
					}}>
					{level?.name}
				</h1>
			</OverlayTrigger>
		</div>
	);
};

const ButtonActions = ({ handleRollMore }) => {
	const { level, setLevel, game, setCurrentModal } = useContext(LevelContext);
	const { currentUserData } = useContext<any>(UserContext);
	const [clickedPlay, setClickedPlay] = useState(false);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const currentUserId = currentUserData?._id;
	const history = useHistory();
	const query = useQuery();

	const isAuthor = level?.author?._id === currentUserId;
	const otherAuthor = level?.access?.includes(currentUserId);
	const isStaff = currentUserData?.staffSession;

	const favourited = level?.hasFavourited ?? false;
	const bookmarked = level?.hasBookmarked ?? false;

	// playing a level
	const handlePlayLevel = useCallback(
		async (e) => {
			e.preventDefault();
			let levelData = level?.data;
			let extension = level?.extension;
			const hasWebVersion = game?.silentHtml5 || game?.native;

			// get level code/file if it doesnt exist
			if (!levelData) {
				const msg = toast.loading("Fetching content...");
				const response = await getLevelData(level?._id);
				toast.dismiss(msg);
				levelData = response?.data?.levelData;
				extension = response?.data?.extension;
				// cache response
				setLevel((prev) => ({ ...prev, data: levelData, extension }));
				// success check
				if (response?.status !== 200)
					return toast.error(
						response?.data?.responseMessage ||
							"Failed to retrieve level data.",
					);
			}

			if (timeoutRef.current) clearTimeout(timeoutRef.current);

			// download or copy
			if (levelData?.type === "Buffer") {
				downloadFile(
					new Blob([new Uint8Array(levelData?.data)], {
						type: "application/zip",
					}),
					{ ...level, extension },
				);
				return toast.success("Level downloaded successfully!");
			} else {
				try {
					if (hasWebVersion) setClickedPlay(true);
					await navigator.clipboard.writeText(levelData);
					timeoutRef.current = setTimeout(
						() => setClickedPlay(false),
						15 * 1000,
					); // revert after 20 seconds
				} catch (err) {
					toast.error("Failed to copy level code.");
					throw err;
				}
			}

			// handle a shift click
			if ((e.ctrlKey || window.innerWidth < 992) && hasWebVersion) {
				toast.success("Copied level code! Opening game...");
				return setTimeout(
					() =>
						window.open(game?.url, "_blank", "noopener,noreferrer"),
					1000,
				);
			}

			// send level code into a channel
			const msg = { type: "play", code: levelData };
			const bc = new BroadcastChannel("LSS");

			// If channel is newly created
			bc.postMessage(msg);
			bc.close();

			// open in new tab if no response received
			if (clickedPlay && !e.shiftKey && hasWebVersion) {
				toast.success("Opening game...");
				return setTimeout(
					() =>
						window.open(game?.url, "_blank", "noopener,noreferrer"),
					1000,
				);
			}

			toast.success(
				hasWebVersion
					? "Copied code! Click again to open game (Shift click to copy only)"
					: "Copied code!",
			);
		},
		[level, game, setLevel, clickedPlay],
	);

	const ButtonsToMap = useMemo(() => {
		const allButtons = [
			{
				name: "Roll next",
				icon: "casino",
				tooltip: "Roll the next levels for your query",
				action: handleRollMore,
				show: query.get("random") === "1",
				type: 1,
			},
			{
				name: clickedPlay ? "Open game" : "Play",
				icon: "sports_esports",
				tooltip: clickedPlay
					? "Open game in a new tab" +
						(game?.native || game?.silentHtml5
							? " (Shift click to only copy code)"
							: "")
					: "Play this level!" +
						(game?.native || game?.silentHtml5
							? " (Control click to also open game)"
							: ""),
				action: handlePlayLevel,
				show: true,
				type: clickedPlay ? 1 : 0,
			},
			{
				name: "Share",
				icon: "group_add",
				tooltip:
					"Share this level with other users or invite friends to edit",
				action: () => setCurrentModal(1),
				show: isAuthor || isStaff,
				type: 1,
			},
			{
				name: "Edit",
				icon: "edit",
				tooltip: "Edit level/page content",
				href: `/levels/edit/${level?._id}`,
				action: (e) => pushHistory(history, e),
				show: isAuthor || isStaff || otherAuthor,
				type: 0,
			},
			{
				name: bookmarked ? "Unbookmark" : "Bookmark",
				icon: bookmarked ? "bookmark_remove" : "bookmark_add",
				tooltip: `${bookmarked ? "Remove this level from" : "Add this level to"} your pivate bookmarks list`,
				action: () => {
					// ad for creating an account
					if (!currentUserData?._id)
						return toast.info(
							"Logging in will grant you access to bookmarks, favourites and more!",
							{
								onClick: () => {
									pushHistory(history, "/auth");
									window.scrollTo(0, 0);
								},
								className: "clickable",
							},
						);
					const msg = toast.loading("Processing...");
					bookmarkLevel(level?._id)
						.then(() =>
							setLevel((prev) => ({
								...prev,
								hasBookmarked: !prev?.hasBookmarked,
							})),
						)
						.finally(() => toast.dismiss(msg));
				},
				show: true,
				type: bookmarked ? 2 : 0,
			},
			{
				name: favourited ? "Unfavourite" : "Favourite",
				icon: favourited ? "heart_broken" : "favorite",
				tooltip: `${favourited ? "Remove this level from" : "Add this level to"} your public favourites list`,
				action: () => {
					const msg = toast.loading("Processing...");
					favouriteLevel(level?._id)
						.then(() =>
							setLevel((prev) => ({
								...prev,
								hasFavourited: !prev?.hasFavourited,
							})),
						)
						.finally(() => toast.dismiss(msg));
				},
				show: currentUserData?._id,
				type: favourited ? 2 : 1,
			},
			{
				name: "Report",
				icon: "report",
				tooltip:
					"Report breaking of the rules of this level to the moderation team",
				href: `/report?id=${level?._id}&type=level`,
				action: (e) => pushHistory(history, e),
				show:
					currentUserData?._id !== level?.author &&
					!currentUserData?.staffBaseView &&
					currentUserData?._id,
				type: 2,
			},
		];

		// only display buttons that are available
		return allButtons.filter((button) => button.show);
	}, [
		game,
		handlePlayLevel,
		history,
		isAuthor,
		isStaff,
		level,
		otherAuthor,
		favourited,
		bookmarked,
		currentUserData?._id,
		currentUserData?.staffBaseView,
		setLevel,
		query,
		handleRollMore,
		setCurrentModal,
		clickedPlay,
	]);

	const tooltipName = "flex-action-level-button-";

	return (
		<>
			{ButtonsToMap.map((button, index) => {
				const typeMap = {
					0: "",
					1: "__special",
					2: "__danger",
				};
				const innerElement = createElement(
					button.href ? "a" : "button",
					{
						...(button.href && { href: button.href }),
						target: "_blank",
						rel: "noopener noreferrer",
						className: `btn btn-primary${typeMap[button?.type]} flex-action-level-button`,
						onClick: button.action,
						children: [
							<span
								className="notranslate material-icons"
								key={tooltipName + "inner1-" + index}>
								{button.icon}
							</span>,
							<span
								className="d-lg-block d-none"
								key={tooltipName + "inner2-" + index}>
								{button.name}
							</span>,
						],
					},
				);

				return (
					<Fragment key={tooltipName + index + "-" + button?.type}>
						<OverlayTrigger
							delay={{ show: 200, hide: 200 }}
							overlay={
								<Tooltip id={tooltipName + "tooltip"}>
									<div className="level-page-tooltip">
										{button.tooltip}
									</div>
								</Tooltip>
							}
							placement="top">
							{innerElement}
						</OverlayTrigger>
					</Fragment>
				);
			})}
		</>
	);
};

const AccessManageModal = ({ usersWithAccess, setUsersWithAccess }) => {
	const history = useHistory();
	const { level, setLevel, setCurrentModal, setModalSize } =
		useContext(LevelContext);

	const shareState = level?.shareState;

	const [userInput, setUserInput] = useState("");
	const [currentShareState, setCurrentShareState] = useState(shareState);
	const [lockCommentSubmit, setLockCommentSubmit] = useState(false);

	const shareStateConstants = LevelConstants.ShareStates;
	const statusConstants = LevelConstants.Statuses;
	const levelID = level?._id;
	const currentInviteShareState =
		currentShareState === shareStateConstants.INVITE;
	const inviteShareState = shareState === shareStateConstants.INVITE;

	const publicLevel = [
		statusConstants.PUBLIC,
		statusConstants.FEATURED,
	].includes(level?.status);

	const handleManageAccess = (e) => {
		if ((e.type === "click" || e.key === "Enter") && !lockCommentSubmit) {
			if (level?.shareState === currentShareState) {
				setLockCommentSubmit(true);
				manageLevelAccess(levelID, {
					mode: "users",
					target: userInput,
					force: "add",
				})
					.then((response) => {
						setLockCommentSubmit(false);
						if (response) {
							setLevel((prev) => ({
								...prev,
								access: [
									...(prev?.access || []),
									response.data.user,
								],
							}));
							setUserInput("");
							setUsersWithAccess((prevState) => [
								...prevState,
								response.data.user,
							]);
						}
					})
					.catch(() => setLockCommentSubmit(false));
			} else
				toast.error(
					"Please save your changes before adding/removing any people.",
				);
		}
	};

	useEffect(() => {
		setModalSize(currentInviteShareState ? "xl" : undefined);
		if (
			currentInviteShareState &&
			!usersWithAccess?.length &&
			level?.access?.length
		) {
			setModalSize("xl");
			getUsersWithAccess(levelID).then((response) => {
				if (response?.data) {
					setUsersWithAccess(response?.data?.users || []);
				}
			});
		}
	}, [
		shareState,
		levelID,
		setModalSize,
		usersWithAccess,
		setUsersWithAccess,
		currentInviteShareState,
		level?.access?.length,
	]);

	const specialAccessDisplays = {
		[shareStateConstants.INVITE]: (
			<>
				<hr />
				<input
					type="text"
					className="access-form"
					name="manage-users"
					title="invite users"
					maxLength={24}
					style={{ borderRadius: "8px 0 0 8px" }}
					disabled={!inviteShareState}
					placeholder={
						inviteShareState
							? "Invite by username or ID"
							: "Please click save first"
					}
					value={userInput}
					onKeyDown={(e) => {
						handleManageAccess(e);
					}}
					onChange={(e) => {
						setUserInput(e.target.value);
					}}
				/>
				<OverlayTrigger
					placement="top"
					overlay={<Tooltip id="addUser">Add user</Tooltip>}>
					<button
						className="btn btn-primary"
						style={{
							marginBottom: "1px",
							height: "31px",
							width: "36px",
							borderRadius: "0 8px 8px 0",
						}}
						type="button"
						id="manage-users"
						name="manage-users"
						disabled={
							!userInput || lockCommentSubmit || !inviteShareState
						}
						onClick={(e) => {
							handleManageAccess(e);
						}}>
						<span
							className=" notranslate material-icons"
							style={{
								position: "relative",
								bottom: "5px",
								right: "9px",
								fontSize: "30px",
							}}>
							add
						</span>
					</button>
				</OverlayTrigger>
				<div>
					{usersWithAccess?.length ? (
						<div className="user-access-list">
							{usersWithAccess?.map((user) => (
								<MappedUsersWithAccess
									level={level}
									setLevel={setLevel}
									setUsersWithAccess={setUsersWithAccess}
									user={user}
								/>
							))}
						</div>
					) : (
						<>
							<hr style={{ marginBottom: "4px" }} />
							<i className="gray">No users have been invited</i>
						</>
					)}
				</div>
			</>
		),
		[shareStateConstants.PUBLIC]: (
			<div>
				<hr />
				{!publicLevel
					? "You can publish your level on the edit level page"
					: "Click the button below to copy the link to your level!"}
				<br />
				<br />
				{!publicLevel ? (
					<a
						href={`/levels/edit/${levelID}?scrollTo=visibility`}
						onClick={(e) => {
							e.preventDefault();
							history.push(
								`/levels/edit/${levelID}?scrollTo=visibility`,
							);
						}}>
						<button className="btn btn-primary">
							Publish my level
						</button>
					</a>
				) : (
					<button
						className="btn btn-primary"
						onClick={() =>
							navigator.clipboard
								.writeText(window.location.href)
								.then(() =>
									toast.success("Copied link to clipboard!"),
								)
						}>
						Copy link
					</button>
				)}
			</div>
		),
	};

	const shareStateOptions = [
		{
			value: shareStateConstants.PUBLIC,
			label: "Public",
			show: true,
		},
		{
			value: shareStateConstants.PRIVATE,
			label: "Private",
			show: !publicLevel,
		},
		{
			value: shareStateConstants.LINK,
			label: "Members with link",
			show: !publicLevel,
		},
		{
			value: shareStateConstants.FRIENDS,
			label: "Friends only",
			show: !publicLevel,
		},
		{
			value: shareStateConstants.INVITE,
			label: "Invite only",
			show: true,
		},
	];

	return (
		<span className="modal-fill">
			<Modal.Body
				className="modal-filter"
				style={{
					maxHeight: "80vh",
					textAlign: "center",
					overflowY: "auto",
				}}>
				<h3 className="yellow">
					Share {!publicLevel && "private"} access with friends
				</h3>
				<label htmlFor="select-access">Access mode</label>
				<br />
				<select
					id="select-access"
					name="select-access"
					className="access-form"
					value={currentShareState}
					onChange={(e) => setCurrentShareState(e.target.value)}>
					{shareStateOptions
						.filter((option) => option.show)
						.map((option, key) => (
							<option
								key={option.value + "-" + key + "-sharestates"}
								value={option.value}>
								{option.label}
							</option>
						))}
				</select>
				{specialAccessDisplays?.[currentShareState] || null}
			</Modal.Body>
			<Modal.Footer className="mx-auto modal-buttons">
				<Button
					className="btn-primary__special"
					style={{
						display:
							currentShareState === level?.shareState ||
							currentShareState === shareStateConstants.PUBLIC
								? "none"
								: "block",
					}}
					onClick={() => {
						const msg = toast.loading("Changing access...");
						manageLevelAccess(levelID, {
							mode: "visibility",
							target: currentShareState,
						})
							.then((response) => {
								setLevel((prevState) => {
									return {
										...prevState,
										shareState: response.data.shareState,
									};
								});
							})
							.finally(() => toast.dismiss(msg));
					}}>
					Save
				</Button>
				<Button
					className="btn-primary__danger"
					onClick={() => setCurrentModal(0)}>
					Close
				</Button>
			</Modal.Footer>
		</span>
	);
};

const MappedUsersWithAccess = ({
	user,
	setLevel,
	level,
	setUsersWithAccess,
}) => {
	const levelID = level?._id;
	const history = useHistory();
	const isAuthor = level?.otherAuthors?.includes(user?._id);

	const removeAccess = () => {
		manageLevelAccess(levelID, {
			mode: "users",
			target: user._id,
			force: "remove",
		}).then((response) => {
			if (response) {
				setLevel((prev) => ({
					...prev,
					access: prev.access.filter((u) => {
						return u !== user._id;
					}),
				}));
				setUsersWithAccess((prevState) =>
					prevState.filter((u) => u._id !== user._id),
				);
			}
		});
	};

	return (
		<div key={user?._id} className="hover-colour">
			<div>
				<span
					className="main-roles"
					style={{
						color: mapHighestRole(user?.mainRoles, "color"),
						borderColor: mapHighestRole(user?.mainRoles, "color"),
						boxShadow: `0 0 1px 1px ${mapHighestRole(
							user?.mainRoles,
							"color",
						)}`,
						position: "relative",
					}}>
					{mapHighestRole(user?.mainRoles, "role")}
				</span>
				<a
					href={`/users/${user?._id}`}
					className={isAuthor ? "yellow" : ""}
					style={{ marginLeft: "8px" }}
					title={
						isAuthor
							? "You are sharing xp with this user since they have made direct changes to this level"
							: "This user hasn't made any changes to this level (yet)"
					}
					onClick={(e) => {
						pushHistory(history, e);
					}}>
					{user.username}
				</a>
			</div>
			<div>
				<ActionButton
					tooltip={"Revoke access"}
					toolTipPlacement={"left"}
					icon={"person_off"}
					hoverColour="rgb(103, 0, 0)"
					colour="rgb(255, 0, 0)"
					onClick={removeAccess}
				/>
			</div>
		</div>
	);
};

const RandomLevelModal = ({
	levels,
	setLevel,
	setCurrentModal,
	setNoReveal,
	fetchLevelLogic,
}) => {
	const history = useHistory();
	const location = useLocation();

	return (
		<span className="modal-fill">
			<Modal.Body
				className="modal-filter"
				style={{
					textAlign: "center",
					maxHeight: "85vh",
					overflowY: "auto",
					overflowX: "hidden",
					paddingBottom: "20px",
				}}>
				<h3 className="yellow">Pick a level!</h3>
				<hr />
				<div
					style={{
						display: "flex",
						flexFlow: "row",
						alignItems: "center",
						justifyContent: "center",
						flexWrap: "wrap",
						gap: "12px",
						padding: "12px",
					}}>
					{levels.map((level, key) => (
						<div
							key={key}
							title={level?.name}
							className="image-star-container-base random-level"
							onClick={() => {}}>
							<ImageStarContainer
								level={level}
								hoverEvents={true}
								authorClick={false}
								clickHandler={(e) => {
									e.preventDefault();
									e.stopPropagation();
									setLevel(level);
									setCurrentModal(0);
									setNoReveal(0);
									history.push({
										pathname: `/levels/${level?._id}`,
										search: location?.search,
									});
									// fetch a fresh instance to include the proper parameters
									fetchLevelLogic(level?._id);
								}}
							/>
						</div>
					))}
				</div>
			</Modal.Body>
		</span>
	);
};

const UserRatesModal = ({
	setRatesToMap,
	ratesToMap,
	voteAuthors,
	setVoteAuthors,
	numberOfVotePages,
	setNumberOfVotePages,
	ratepage,
	setRatepage,
}) => {
	const { currentUserData } = useContext(UserContext);
	const { level, setLevel, setModalSize, setCurrentModal } =
		useContext(LevelContext);

	const [voteAuthorsChanged, setVoteAuthorsChanged] =
		useState<boolean>(false);

	const [prevRatepage, setPrevRatepage] = useState<number>(ratepage);
	const [isQueryingAuthors, setIsQueryingAuthors] = useState<boolean>(false);
	const limit = 20;

	useEffect(() => {
		setModalSize("lg");
		return () => {
			setModalSize("sm");
		};
	}, [currentUserData?.staffSession, setModalSize]);

	useEffect(() => {
		if (
			level?.rates &&
			(!ratesToMap?.length || ratepage !== prevRatepage)
		) {
			setVoteAuthorsChanged(true);
		}
	}, [level?.rates, ratepage, prevRatepage, ratesToMap?.length]);

	useEffect(() => {
		if (level?.rateChanged) {
			// reset!
			setRatepage(1);
			setVoteAuthorsChanged(true);
			setLevel((prev) => ({ ...prev, rateChanged: false }));
		}
	}, [level?.rateChanged, setLevel, setRatepage]);

	// useEffect hook for getting authors, staff only
	useEffect(() => {
		if (isQueryingAuthors) return setVoteAuthorsChanged(false);

		if (voteAuthorsChanged) {
			setPrevRatepage(ratepage);
			setIsQueryingAuthors(true);
			getUserSet({ page: ratepage, levelID: level?._id })
				.then((response) => {
					if (response?.data) {
						setVoteAuthors(response.data.users);
						setRatesToMap(response.data?.rates);
						setNumberOfVotePages(response.data?.numberOfPages);
					}
				})
				.finally(() => setIsQueryingAuthors(false));
		}
	}, [
		voteAuthorsChanged,
		ratepage,
		prevRatepage,
		level?.rates,
		level?._id,
		currentUserData,
		isQueryingAuthors,
		setRatesToMap,
		setVoteAuthors,
		setVoteAuthorsChanged,
		setModalSize,
		setNumberOfVotePages,
	]);

	return (
		<span className="modal-fill">
			<Modal.Body
				className="center-textoutput modal-filter modal-bdy"
				style={{ fontSize: "20px" }}>
				<h1 style={{ textAlign: "center" }} className="yellow">
					Vote logs
				</h1>
				{isQueryingAuthors ? (
					<div style={{ marginTop: "16px" }}>Querying raters...</div>
				) : (ratesToMap?.length &&
						voteAuthors?.length &&
						level?.rates) ||
				  (!currentUserData?.isStaff &&
						level?.rates &&
						ratesToMap?.length) ? (
					<div className={`rates-modal`}>
						{ratesToMap.map((rate, key) => (
							<Fragment key={"level-page-rates-display-" + key}>
								<MappedRates
									index={key + limit * (prevRatepage - 1)}
									rate={rate}
									content={level}
									authors={voteAuthors}
									setAuthors={setVoteAuthors}
									levelID={level?._id}
									setLevel={setLevel}
									setRatesToMap={setRatesToMap}
								/>
							</Fragment>
						))}
					</div>
				) : (
					<div style={{ marginTop: "16px" }}>
						This level hasn't been rated yet.
					</div>
				)}
				<br />
				{level?.rates?.length ? (
					<Paginate
						type="noparams"
						numberOfPages={numberOfVotePages || 1}
						noparampage={ratepage}
						setPage={setRatepage}
					/>
				) : null}
			</Modal.Body>
			<Modal.Footer className="mx-auto modal-filter modal-buttons">
				<Button onClick={() => setCurrentModal(0)}>Close</Button>
			</Modal.Footer>
		</span>
	);
};

const MappedRates = ({
	index,
	rate,
	content,
	authors,
	levelID,
	setLevel,
	setRatesToMap,
	setAuthors,
}) => {
	const { currentUserData } = useContext<any>(UserContext);
	const [deleteState, setDeleteState] = useState<boolean>(false);

	const history = useHistory();
	const author = authors?.find((author) => author?.index === index);
	const hasNullId = !author?._id;
	const originalRate = parseInt(content?.rates[index]);

	const handleClick = (e) => {
		e.preventDefault();
		// return error if rate is deleted.

		if (!deleteState) {
			// set delete state to true
			setDeleteState(true);
			// reset after 3 seconds
			setTimeout(() => setDeleteState(false), 3000);
			return toast.warn("Click again to confirm permanent removal.");
		}
		// perform the backend call if delete state is true
		removeLevelRate(levelID, { user: author?._id, index })
			.then((response) => {
				// update the state of the level
				const data = response?.data;
				setDeleteState(false);
				// update the level
				setLevel((prevState) => {
					return {
						...prevState,
						rating: data.rating,
						rates: prevState.rates.filter(
							(_, key) => key !== index,
						),
						...(author?._id === currentUserData?._id && {
							hasRated: 0,
						}),
					};
				});
				// remove the rate from the map
				setRatesToMap((prevState) =>
					prevState.filter((_, key) => key !== index),
				);
				// remove the author from the map
				setAuthors((prevState) =>
					prevState.filter((user) => user?._id !== author?._id),
				);
			})
			.catch(() => {
				// reset delete state
				setDeleteState(false);
			});
	};

	return (
		<div className="unselectable hover-colour">
			<div
				style={{
					color: deleteState ? "red" : "",
					transition: "color 0.3s ease-in-out",
					whiteSpace: "nowrap",
				}}>
				#{index + 1}
			</div>
			<div>
				<MapStars
					key={rate}
					level={null}
					rate={rate}
					width="10mm"
					height="10mm"
					top="0"
					starCount={5}
					color={
						deleteState
							? "#c70700"
							: originalRate === 5
								? "#2cd40f"
								: ""
					}
				/>
			</div>
			<div>
				{hasNullId ? (
					<i className=" gray">{author?.username || "Hidden"}</i>
				) : (
					<a
						href={`/users/${author?._id}`}
						style={{
							color: deleteState ? "red" : "",
							transition: "color 0.3s ease-in-out",
						}}
						onClick={(e) => pushHistory(history, e)}>
						{author?.username}
					</a>
				)}
			</div>
			<div>
				<OverlayTrigger
					overlay={
						<Tooltip id="removeRate">
							{deleteState
								? "Confirm removal"
								: "Remove star rate"}
						</Tooltip>
					}>
					<a
						href="#!"
						className={`notranslate material-icons thread-icon ${
							deleteState
								? "hydrawisp-red"
								: hasNullId
									? "gray"
									: ""
						}`}
						style={{
							transition: "color 0.3s ease-in-out",
							pointerEvents: hasNullId ? "none" : "auto",
						}}
						onClick={(e) => handleClick(e)}>
						{hasNullId ? "not_interested" : "delete"}
					</a>
				</OverlayTrigger>
			</div>
		</div>
	);
};

const LevelBody = ({ setFocusImage, cacheBustStr }) => {
	const { currentUserData } = useContext(UserContext);
	const { level, game, setCurrentModal } = useContext(LevelContext);
	const [scrolled, setScrolled] = useState<boolean>(false);
	const scrollRef = useRef<HTMLDivElement>(null);

	const intervalRef = useRef<NodeJS.Timeout | null>(null);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const validVideo = !!level?.video;

	const featuredLevel = level?.status === LevelConstants.Statuses.FEATURED;

	const featuredIcon = featuredLevel
		? [
				{
					icon: "help",
					tooltip:
						"Playing, rating and commenting on this level grants 40% additional base experience.",
				},
			]
		: [];
	const unfeaturedIcon =
		(featuredLevel && currentUserData?.isStaff) ||
		(level?.previouslyFeatured && !featuredLevel)
			? [
					{
						icon: "info",
						tooltip: `Featured on ${moment(level?.featureDate).format("MMM D, YYYY - h:mm A")}. ${featuredLevel ? "Will be unfeatured on" : "Unfeatured on"} ${moment(level?.unfeatureDate).format("MMM D, YYYY - h:mm A")}.`,
					},
				]
			: [];

	const lastEditedIsPostdate = level?.lastEdited
		? new Date(level?.lastEdited) > new Date(level?.postDate)
		: false;
	const lvlStatuses = LevelConstants.Statuses;

	const scrollCarouselLogic = useCallback(() => {
		if (scrollRef?.current && validVideo) {
			intervalRef.current = setInterval(() => {
				const el = scrollRef.current;
				if (!el) return;
				if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 50) {
					el.scrollTo({ left: 0, behavior: "smooth" });
				} else {
					el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
				}
			}, 6500);
		}
	}, [validVideo]);

	useEffect(() => {
		// initial start of carousel
		if (scrolled) {
			timeoutRef.current = setTimeout(() => {
				scrollCarouselLogic();
				setScrolled(false);
			}, 3500);
		} else {
			scrollCarouselLogic();
		}

		// cleanup
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
		};
	}, [validVideo, scrolled, scrollCarouselLogic]);

	useEffect(() => {
		YouTubeIframeLoader.load((YT) => {
			new YT.Player("video-player", {
				videoId: level?.video,
				events: {
					onStateChange: (event) => {
						if (event.data === YT.PlayerState.PLAYING) {
							// stop carousel while video plays
							if (intervalRef.current) {
								clearInterval(intervalRef.current);
								intervalRef.current = null;
							}
							if (timeoutRef.current) {
								clearTimeout(timeoutRef.current);
								timeoutRef.current = null;
							}
							return;
						}
						if (
							event.data === YT.PlayerState.PAUSED ||
							event.data === YT.PlayerState.ENDED
						) {
							// resume carousel after pause/end
							if (!intervalRef.current && !timeoutRef.current) {
								scrollCarouselLogic();
							}
						}
					},
				},
			});
		});
	}, [level?.video, scrollCarouselLogic]);

	const statsToDisplay = [
		{
			tooltip: "Tags",
			value:
				level?.tags?.length && level.tags[0].trim() !== "" ? (
					level.tags.map((tag, i) => (
						<React.Fragment key={i}>
							<span className="tags">{tag}</span>
							{i < level.tags.length - 1 && " "}
						</React.Fragment>
					))
				) : (
					<i className="gray">No tags</i>
				),
			styleOverrides: {
				display: "flex",
				flexDirection: "row",
				gap: "4px",
				flexWrap: "wrap",
				whiteSpace: "pre-wrap",
			},
			icon: "local_offer",
		},
		{
			tooltip: "Game Version",
			value: level?.gameVersion ? (
				`${game?.acronym} ${level?.gameVersion}`
			) : (
				<i className="gray">Unknown</i>
			),
			icon: "api",
		},
		{
			tooltip: "Difficulty",
			value: level?.difficulty,
			icon: "extension",
		},
		{
			tooltip: "Plays",
			value: level?.plays,
			icon: "sports_esports",
			name: LevelConstants.LevelStats.PLAYS.name,
		},
		{
			tooltip: "Rates",
			value: level?.rates?.length,
			icon: "star",
			button: {
				text: "view",
				disabled: !level?.rates?.length,
				onClick: () => {
					setCurrentModal(3);
				},
			},
			name: LevelConstants.LevelStats.RATES.name,
		},
		{
			tooltip: "Favourites",
			value: level?.favourites || 0,
			icon: "favorite",
			name: LevelConstants.LevelStats.FAVOURITES.name,
		},
		{
			tooltip: "Commenters",
			value: level?.commenters?.length,
			icon: "rate_review",
			name: LevelConstants.LevelStats.COMMENTERS.name,
		},
		{
			tooltip: "Visibility",
			value: level?.status,
			icon: {
				[lvlStatuses.PUBLIC]: "public",
				[lvlStatuses.PRIVATE]: "lock",
				[lvlStatuses.FEATURED]: "auto_awesome",
				[lvlStatuses.DELETED]: "delete",
			}[level?.status],
			additionalIcons: [...featuredIcon, ...unfeaturedIcon],
			color: {
				[lvlStatuses.PUBLIC]: LevelConstants.StatusColors.PUBLIC,
				[lvlStatuses.PRIVATE]: LevelConstants.StatusColors.PRIVATE,
				[lvlStatuses.FEATURED]: LevelConstants.StatusColors.FEATURED,
				[lvlStatuses.DELETED]: LevelConstants.StatusColors.DELETED,
			}[level?.status],
			styleOverrides: {
				display: "flex",
				alignItems: "center",
				gap: "3px",
			},
		},
		{
			tooltip: "Publish Date",
			value: moment(level?.postDate).format("MMM D, YYYY - h:mm A"),
			icon: "publish",
		},
		...(lastEditedIsPostdate
			? [
					{
						tooltip: "Last edited",
						value: moment(level?.lastEdited).format(
							"MMM D, YYYY - h:mm A",
						),
						icon: "edit_calendar",
					},
				]
			: []),
		{
			tooltip: "Contributions",
			value:
				level?.contributors?.length > 0 ? (
					level?.contributors.join(", ")
				) : (
					<i className="gray">None</i>
				),
			icon: "groups",
		},
	];

	const activeStats = getActiveStats(level);

	const thumbnailEl = (
		<img
			src={level?.thumbnail + cacheBustStr || DefaultThumbnail}
			alt="Level Thumbnail"
			onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
				const target = e.currentTarget as HTMLImageElement;
				target.src = DefaultThumbnail;
			}}
		/>
	);

	const mapStarsEl = (
		<div style={{ width: "max-content" }}>
			<MapStars
				level={level}
				rate={null}
				width="12.5mm"
				height="12.5mm"
				top="0"
				starCount={5}
				color={
					parseFloat(level?.rating) >= 4.9 && level?.rates?.length > 5
						? "#2cd40f"
						: ""
				}
				style={{ marginLeft: "0" }}
			/>
		</div>
	);

	const handleWheel = (e) => {
		e.stopPropagation();
		const el = scrollRef.current;
		if (!el) return;
		if (e.deltaY < 0) {
			scrollRef.current?.scrollBy({
				left: -e.deltaY * 2,
				behavior: "smooth",
			});
		} else {
			el.scrollTo({ left: 0, behavior: "smooth" });
		}
	};

	return (
		<div className="level-body">
			<div>
				<div
					className="carousel-container"
					ref={scrollRef}
					onTouchMove={() => {
						setScrolled(true);
					}}
					onWheel={handleWheel}>
					<div>
						<div onClick={() => setFocusImage((p) => !p)}>
							{thumbnailEl}
							{mapStarsEl}
						</div>
						{validVideo && (
							<div>
								<div id="video-player" />
							</div>
						)}
					</div>
				</div>
				<div className="side-grid">
					<div className="stats-container">
						<LevelStatsDisplay
							statsToDisplay={statsToDisplay}
							activeStats={activeStats}
						/>
					</div>
					<div className="action-button-container">
						<ActionButtonDisplay />
					</div>
				</div>
			</div>
		</div>
	);
};

const LevelStatsDisplay = ({ statsToDisplay, activeStats }) =>
	statsToDisplay.map((stat, key) => {
		const styleOverrides = stat?.styleOverrides ?? {};
		const additionalIcons = stat?.additionalIcons ?? [];
		const activeStat = activeStats?.[stat?.name];
		const color = stat?.color ?? activeStat?.color ?? "";
		const button = stat?.button ? (
			<button
				className="btn btn-primary"
				disabled={stat.button.disabled}
				style={{
					height: "24px",
					marginLeft: "8px",
					display: "inline-flex",
					alignItems: "center",
				}}
				onClick={stat.button.onClick}>
				{stat.button.text}
			</button>
		) : null;

		return (
			<Fragment key={"level-page-stats-display-" + key}>
				<div className="unselectable">
					<OverlayTrigger
						delay={{
							show: 250,
							hide: 250,
						}}
						placement={window.innerWidth < 1024 ? "top" : "left"}
						overlay={
							<Tooltip id={stat.tooltip + key}>
								{activeStat?.prependText}
								{stat.tooltip}
							</Tooltip>
						}>
						<span>
							<span
								className="notranslate material-icons-round"
								style={{ color }}>
								{stat.icon}
							</span>
							<span style={styleOverrides}>{stat.value}</span>
						</span>
					</OverlayTrigger>
					<span>
						{additionalIcons?.map((icon, key) => (
							<Fragment
								key={"additional-icon-" + stat?.tooltip + key}>
								<OverlayTrigger
									delay={{
										show: 250,
										hide: 250,
									}}
									placement="top"
									overlay={
										<Tooltip id={icon.tooltip + key}>
											<span className="level-page-tooltip">
												{icon.tooltip}
											</span>
										</Tooltip>
									}>
									<span className="notranslate material-icons-round">
										{icon.icon}
									</span>
								</OverlayTrigger>
							</Fragment>
						))}
						{button}
					</span>
				</div>
			</Fragment>
		);
	});

const ActionButtonDisplay = () => {
	const { currentUserData } = useContext(UserContext);
	const { level, setLevel, setCurrentModal, currentModal } =
		useContext(LevelContext);

	const [isDoingAction, setIsDoingAction] = useState(false);
	// state selectors holding rotation
	const [delResProps, setDelResProps] = useState(0); // used for both delete and restore
	const [oblProps, setOblProps] = useState(0); // used for obliterating
	const [privateProps, setPrivateProps] = useState(0); // used for privating button

	const toastRef = useRef<null | string | Id>(null);
	const history = useHistory();

	const isStaff = !!currentUserData?.isStaff || false;
	const isMainAuthor =
		(level?.author?._id === currentUserData?._id &&
			!!currentUserData?._id) ||
		false;
	const isAuthor =
		isMainAuthor || level?.otherAuthors?.includes(currentUserData?._id);
	const starGazable =
		!!currentUserData?.subRoles?.includes(
			UserConstants.SubRoles.STARGAZER,
		) && !isStaff;

	const lvlStatuses = LevelConstants.Statuses;
	const deletedLevel = level?.status === lvlStatuses.DELETED;
	const publicLevel = level?.status === lvlStatuses.PUBLIC;
	const featuredLevel = level?.status === lvlStatuses.FEATURED;

	// access the executeparams to make an api call
	const handleUpdateLevel = (valueObj) => {
		const params = valueObj?.executeParams;
		setIsDoingAction(true); // disable buttons
		const msg = toast.loading(params.toast);
		valueObj?.setValue?.(0);
		setTimeout(
			() =>
				params
					.request(level._id) // api call
					.then(() => {
						setLevel((prev) => ({
							...prev, // update the state of the current level
							status: lvlStatuses?.[params.status],
						}));
						params?.extraFunc?.(); // optional functions afterwards
					})
					.finally(() => {
						// reenable buttons and remove popup msg
						setIsDoingAction(false);
						toast.dismiss(msg);
					}),
			100,
		);
	};

	// the values belonging to each action button displayed below which will be used for handling pressing buttons and their associated function
	const actionTypeValues = {
		delete: {
			value: delResProps,
			setValue: setDelResProps,
			max: 4,
			divider: 90,
			type1: "deletion",
			toastFunc1: toast.warn,
			execute: handleUpdateLevel,
			executeParams: {
				toast: "Deleting level...",
				status: "DELETED",
				request: deleteLevel,
				extraFunc: () => {
					if (!isStaff)
						history.push(`/levels/user/${level?.author?._id}`);
				},
			},
		},
		obliterate: {
			value: oblProps,
			setValue: setOblProps,
			max: 4,
			divider: 90,
			type1: "obliteration",
			toastFunc1: toast.warn,
			execute: handleUpdateLevel,
			executeParams: {
				toast: "Obliterating level...",
				request: obliterateLevel,
				extraFunc: () =>
					history.push(`/levels/user/${level?.author?._id}`),
			},
		},
		restore: {
			value: delResProps,
			setValue: setDelResProps,
			max: 2,
			divider: 180,
			type1: "restoration",
			toastFunc1: toast.info,
			execute: handleUpdateLevel,
			executeParams: {
				toast: "Restoring level...",
				status: "PRIVATE",
				request: restoreLevel,
			},
		},
		private: {
			value: privateProps,
			setValue: setPrivateProps,
			max: 3,
			divider: 120,
			type1: "privating",
			toastFunc1: toast.info,
			execute: handleUpdateLevel,
			executeParams: {
				toast: "Privating level...",
				status: "PRIVATE",
				request: privateLevel,
			},
		},
		video: {
			execute: () => setTimeout(() => setCurrentModal(5), 1000),
		},
		feature: {
			execute: () => setTimeout(() => setCurrentModal(4), 500),
		},
	};

	// triggers upon transition end, thus upon click to warn the user or fire the respective function
	const handleActionPress = (type) => {
		setIsDoingAction(false);
		if (toastRef.current) toast.dismiss(toastRef.current);
		const valueObj = actionTypeValues[type];

		const number =
			valueObj?.max - Math.abs(valueObj?.value) / valueObj?.divider;

		// if the rotation is 0, dont toast
		if (number >= valueObj?.max && number) return;

		// otherwise, do toast if rotation hasnt hit its max
		if (number > 0) {
			const message = `Click ${number} more ${number === 1 ? "time" : "times"} to confirm ${valueObj.type1} of this level....`;
			toastRef.current = valueObj.toastFunc1(message);
			return;
		}

		// execute the function if the max is hit
		valueObj.execute(valueObj);
	};

	return (
		<>
			<ActionButton
				tooltip={"Copy level id"}
				rotateMode={3}
				onClick={() =>
					navigator.clipboard
						.writeText(level._id)
						.then(() =>
							toast.success("Copied level id to clipboard."),
						)
						.catch(() => toast.error("Failed to copy level id."))
				}
				icon="copy"
				iconType="material-icons-round"
			/>
			<ActionButton
				tooltip={"Go to event"}
				hoverColour="#bc1fffff"
				colour="#ff2ff8ff"
				show={!!level?.event}
				onClick={() => window.open(`/events/${level?.event}`)}
				icon="celebration"
				iconType="material-icons-round"
			/>
			<ActionButton
				tooltip={"Delete level"}
				show={(isMainAuthor || isStaff) && !deletedLevel}
				disabled={isDoingAction}
				icon="delete"
				stateOverrides={{
					rotation: delResProps,
					setRotation: setDelResProps,
				}}
				rotateMode={1}
				colour="#ff1e1eff"
				hoverColour="#c40000ff"
				resetRotateAfter={3000}
				onTransitionEnd={() => handleActionPress("delete")}
			/>
			<ActionButton
				tooltip={"Obliterate level"}
				show={!!currentUserData?.isAdmin && isStaff && deletedLevel}
				disabled={isDoingAction}
				icon="local_fire_department"
				stateOverrides={{
					rotation: oblProps,
					setRotation: setOblProps,
				}}
				rotateMode={1}
				colour="#d60707ff"
				hoverColour="#9a0707ff"
				resetRotateAfter={3000}
				onTransitionEnd={() => handleActionPress("obliterate")}
			/>
			<ActionButton
				tooltip={"Restore level"}
				show={isStaff && deletedLevel}
				disabled={isDoingAction}
				onClick={() => setIsDoingAction(true)}
				icon="restore_page"
				stateOverrides={{
					rotation: delResProps,
					setRotation: setDelResProps,
				}}
				rotateMode={-5}
				colour="#56b219ff"
				hoverColour="#59e11aff"
				resetRotateAfter={5000}
				onTransitionEnd={() => handleActionPress("restore")}
			/>
			<ActionButton
				tooltip={"Set level to private"}
				show={(isMainAuthor || isStaff) && publicLevel}
				disabled={isDoingAction}
				onClick={() => setIsDoingAction(true)}
				icon="lock"
				stateOverrides={{
					rotation: privateProps,
					setRotation: setPrivateProps,
				}}
				rotateMode={6}
				colour="#9b9b9bff"
				hoverColour="#c6c6c6ff"
				resetRotateAfter={2500}
				onTransitionEnd={() => handleActionPress("private")}
			/>
			<ActionButton
				tooltip={
					level?.video
						? "Change the currently linked youtube video"
						: "Add a youtube video to this level"
				}
				show={
					isStaff ||
					isMainAuthor ||
					!!currentUserData?.subRoles?.includes(
						UserConstants.SubRoles.SCREENSAVER,
					)
				}
				rotateMode={4}
				disabled={currentModal === 5 || isDoingAction}
				onClick={() => handleActionPress("video")}
				icon="ondemand_video"
				iconType="material-icons-round"
				colour={"#009acdff"}
				hoverColour={"#2e77ffff"}
			/>
			<ActionButton
				tooltip={
					featuredLevel
						? "Remove this level from featured"
						: `${starGazable ? "Stargaze" : "Feature"} this level`
				}
				show={
					// show on all for staff and unfeatured for stargazers
					((isStaff || (starGazable && !featuredLevel)) &&
						// make sure its either featured or public & currentuser isnt an author
						!isAuthor &&
						(publicLevel || featuredLevel)) ||
					//* admins with staff session bypass everything
					(!!currentUserData?.isAdmin &&
						isStaff &&
						(publicLevel || featuredLevel))
				}
				rotateMode={-3}
				disabled={currentModal === 4 || isDoingAction}
				onClick={() => handleActionPress("feature")}
				icon="auto_fix_high"
				iconType="material-icons-round"
				colour={featuredLevel ? "#dd4508ff" : "#fcd10fff"}
				hoverColour={featuredLevel ? "#bc3a06ff" : "#fffc4eff"}
			/>
		</>
	);
};

const LevelMiddleBody = () => {
	const { level, setLevel } = useContext(LevelContext);
	const { currentUserData, setCurrentUserData } = useContext(UserContext);

	const allAuthors = [level?.author, ...(level?.otherAuthors || [])];
	const borderStyle = "1px solid rgba(255, 255, 255, 0.3)";
	const loggedIn = !!currentUserData?._id;
	const userIsAuthor = allAuthors?.some(
		(author) => author?._id === currentUserData?._id,
	);

	const [userStarRate, setUserStarRate] = useState<number>(
		Number(level?.hasRated) || 0,
	);
	const [queryStarStore, setQueryStarStore] = useState<number>(0);
	const [waitForConfirmation, setWaitForConfirmation] = useState<boolean>(
		!!level?.hasRated,
	);

	const history = useHistory();

	const authorText = allAuthors?.length > 1 ? "Authors" : "Author";
	const rateText = level?.hasRated
		? "You rated this level."
		: "Rate this level!";

	useEffect(() => {
		if (!level?.hasRated) {
			setUserStarRate(0);
			setWaitForConfirmation(false);
		}
	}, [level?.hasRated]);

	// detect changes in the user's star rating and make an api call if it's different
	useEffect(() => {
		if (
			(!level?.hasRated && userStarRate === 0) ||
			level?.hasRated === userStarRate
		)
			return;

		if (!currentUserData?.verified) {
			setUserStarRate(level?.hasRated || 0);
			toast.warn(
				loggedIn
					? "Click here to verify your account in order to rate levels."
					: "Click here to log in or register in order to rate levels.",
				{
					onClick: () => {
						pushHistory(history, loggedIn ? "/verify" : "/auth");
						window.scrollTo(0, 0);
					},
					className: "clickable",
				},
			);
			return;
		}

		if ((userStarRate < 1 && userStarRate !== 0) || userStarRate > 5) {
			setUserStarRate(level?.hasRated || 0);
			toast.warn("Rating must be between 1 and 5 stars.");
			return;
		}

		if (userStarRate % 0.5 !== 0) {
			setUserStarRate(level?.hasRated || 0);
			toast.warn("Rating must be in multiples of half a star.");
			return;
		}

		if (waitForConfirmation) {
			toast.info("Click 'change rate' to change your star rating");
			return;
		}

		if (queryStarStore !== 0) {
			if (queryStarStore !== userStarRate) {
				setUserStarRate(queryStarStore);
				toast.warn("Just a moment please!");
			}
			return;
		}

		setQueryStarStore(userStarRate);

		// rate the level if everything is in order
		const msg = toast.loading("Rating level...");
		rateLevel(level?._id, { starRate: userStarRate })
			.then((response) => {
				if (response?.data) {
					const data = response?.data;
					// update all appropriate states on the level
					setLevel((prevState) => {
						const newLevel = {
							...prevState,
							rates: data?.rates,
							rating: data.rating,
							hasRated: userStarRate,
							rateChanged: true,
						};
						return newLevel;
					});
					setWaitForConfirmation(true);
					// also update the current user
					setCurrentUserData((prev) => ({
						...prev,
						rates: parseInt(prev?.rates + 1),
					}));
				}
			})
			.finally(() => {
				toast.dismiss(msg);
				setQueryStarStore(0);
			});
	}, [
		userStarRate,
		level,
		currentUserData?.verified,
		history,
		setLevel,
		loggedIn,
		setCurrentUserData,
		queryStarStore,
		waitForConfirmation,
	]);

	// UI for rating a level
	const rateLevelElement = (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				gap: "6px",
			}}>
			<h2 className="center-textoutput">{rateText}</h2>
			<div
				className={
					!level?.hasRated ? "star-map unrated-animation" : "star-map"
				}>
				<MapStars
					level={null}
					rate={userStarRate}
					starCount={5}
					{...(userStarRate === 5 && { color: "#2cd40f" })}
					width={"13.5mm"}
					height={"13.5mm"}
					setState={setUserStarRate}
					style={{ padding: "5px" }}
				/>
			</div>
			<button
				className="btn btn-primary"
				disabled={level?.hasRated === userStarRate}
				style={{ display: waitForConfirmation ? "block" : "none" }}
				onClick={() => setWaitForConfirmation(false)}>
				Change rate
			</button>

			<i className="gray">
				Voting tags will be added in a future site update
			</i>
		</div>
	);
	const xpShareInfoElementIndex =
		!level?.isCollectingXp && level?.totalXpWorth
			? 3
			: userIsAuthor && !level?.access?.length
				? 2
				: level?.otherAuthors?.includes(currentUserData?._id) ||
					  userIsAuthor
					? 1
					: 0;

	const xpShareInfoElement = {
		0: null,
		1: (
			<div className="center-textoutput xp-share-info">
				<h2>Total accumulative rewards</h2>
				All invited authors & the level author will receive equal
				rewards from the level rewards system.{" "}
				{currentUserData?._id === level?.author?._id
					? "Check the dashboard to split the rewards some time after the level is published. Rewards will be shared with all users who have been invited to the level & contributed to it. They will see a similar message on this page and everyone will receive equal rewards."
					: "Ask the author to check the dashboard if the level is published & enough plays/rates/favourites/etc. have accumulated to split the rewards. This message also indicates that you are receiving shared rewards. "}
				<div
					className="yellow center-textoutput"
					style={{ marginTop: "10px" }}>
					Rewards are shared with&nbsp;
					<u>{level?.otherAuthors?.length}</u>&nbsp;other invited
					contributing user(s).
				</div>
			</div>
		),
		2: (
			<div className="center-textoutput xp-share-info">
				<h2>Total accumulative rewards</h2>
				<br />
				Check your dashboard to claim any accumulated rewards some time
				after the level is published.
			</div>
		),
		3: (
			<div className="center-textoutput xp-share-info">
				<h2>Total accumulative rewards</h2>
				<div>
					{Math.round(level?.totalXpWorth * 100) / 100} experience
				</div>
				{
					// author or staff only
					(userIsAuthor || currentUserData?.staffSession) &&
						// ensure the level has collected shimflakes
						level?.collectedRewards?.reduce(
							(a, b) => a + b?.shimflakes,
							0,
						) && (
							<div>
								{Math.round(
									level?.collectedRewards.reduce(
										(a, b) => a + b?.shimflakes,
										0,
									) * 100,
								) / 100}{" "}
								shimflake(s)
							</div>
						)
				}
				<div
					className="wildheart-green center-textoutput"
					style={{ marginTop: "10px" }}>
					Rewards are shared with&nbsp;
					<u>{level?.otherAuthors?.length}</u>&nbsp;other invited
					contributing users.
				</div>
			</div>
		),
	};

	return (
		<div className="level-middlebody">
			<div style={{ borderTop: borderStyle, borderBottom: borderStyle }}>
				{/* cards with author, starrate and xp info start here */}
				<div>
					<h2 className="center-textoutput">{authorText}</h2>
					<div
						className="flex-user-display"
						style={{ justifyContent: "center", gap: "8px" }}>
						{allAuthors?.map((author, key) => (
							<Fragment
								key={
									"level-authors-" + key + "-" + author?._id
								}>
								<UsernameAvatarDisplay user={author} />
							</Fragment>
						))}
					</div>
				</div>
				{!userIsAuthor && rateLevelElement}
				{xpShareInfoElement[xpShareInfoElementIndex]}
			</div>
			<h3 className="center-textoutput yellow">
				<u>Description</u>
			</h3>
			<div className="level-description">
				{level?.description?.length > 0 ? (
					// remove invalid html tags from description and display it
					<ReadMore
						content={level?.description}
						height={500}
						rank={level?.author?.rank}
						noEmbed={true}
					/>
				) : (
					<div className="gray">
						<i>This level has no description set.</i>
					</div>
				)}
			</div>
		</div>
	);
};

const FeatureModal = () => {
	const { level, setCurrentModal, setLevel } = useContext<any>(LevelContext);
	const { currentUserData, setCurrentUserData } =
		useContext<any>(UserContext);

	const [featureDuration, setFeatureDuration] = useState<number>(4);

	const featuredLevel = level?.status === LevelConstants.Statuses.FEATURED;
	const starGazeCharges = currentUserData?.charges?.starGaze?.amount;
	const starGazable =
		currentUserData?.subRoles?.includes(UserConstants.SubRoles.STARGAZER) &&
		!currentUserData?.isStaff;

	const modalStateIndex = featuredLevel ? 3 : starGazable ? 2 : 1;

	const messages = {
		1: "Calling upon distant light...",
		2: "Gazing upon the stars...",
		3: "Shooting down the angel from the sky...",
	};

	const requests = {
		1: () => featureLevel(level?._id, { duration: featureDuration }),
		2: () => featureLevel(level?._id, { duration: 7 }),
		3: () => unfeatureLevel(level?._id),
	};

	const handleUpdate = () => {
		toast.info(messages[modalStateIndex]);
		setCurrentModal(0);
		requests[modalStateIndex]().then(() => {
			// set the level to public or featured
			setLevel((prev) => ({
				...prev,
				status: LevelConstants.Statuses[
					modalStateIndex === 3 ? "PUBLIC" : "FEATURED"
				],
			}));
			// update the user if they are a stargazer
			if (starGazable)
				setCurrentUserData((prev) => ({
					...prev,
					charges: {
						...prev.charges,
						starGaze: {
							...prev.charges.starGaze,
							// reduce their charges by 1
							amount: prev.charges.starGaze.amount - 1,
						},
					},
				}));
		});
	};

	const ModalState = {
		1: (
			<>
				<h4 style={{ textAlign: "center" }}>
					How long should this level be featured for?
				</h4>
				<hr />
				<input
					type="range"
					title="feature duration"
					id="slider"
					name="slider"
					min="1"
					max="14"
					step="1"
					value={featureDuration}
					onChange={(e) =>
						setFeatureDuration(parseInt(e.target.value))
					}
				/>
				<div className="yellow center-textoutput">
					{featureDuration} day
					{featureDuration > 1 && "s"}
				</div>
			</>
		),
		2: (
			<h4 style={{ textAlign: "center" }}>
				Are you sure you wish to gaze upon the stars to feature &nbsp;
				<span className="blue">{level?.name}</span> for 7 days?
			</h4>
		),
		3: (
			<h4 style={{ textAlign: "center" }}>
				Are you sure you want to unfeature &nbsp;
				<span className="blue">{level?.name}</span>?
			</h4>
		),
	};

	return (
		<span className="modal-fill">
			<Modal.Body className="modal-filter">
				{ModalState[modalStateIndex]}
			</Modal.Body>
			<Modal.Footer className="mx-auto modal-buttons modal-filter">
				<Button
					className={
						featuredLevel
							? "btn btn-primary__danger"
							: "btn btn-primary__special"
					}
					disabled={starGazable && !starGazeCharges}
					onClick={handleUpdate}>
					{featuredLevel ? "Yes, unfeature it" : "Confirm"}
					{starGazable && ` (${starGazeCharges || 0}/3 left)`}
				</Button>
				<Button onClick={() => setCurrentModal(0)}>Cancel</Button>
			</Modal.Footer>
		</span>
	);
};

const VideoModal = () => {
	const { level, setCurrentModal, setLevel } = useContext<any>(LevelContext);
	const [videoUrl, setVideoUrl] = useState<string>(level?.video);

	const handleVideoUpdate = (e) => {
		e.preventDefault();
		setCurrentModal(0);
		toast.info("Updating level video...");
		// backend call
		updateLevelVideo({
			id: level?._id,
			video: videoUrl,
		}).then((response) =>
			setLevel((prev) => {
				return { ...prev, video: response?.data?.video };
			}),
		);
	};

	return (
		<span className="modal-fill">
			<Modal.Body>
				<h3 style={{ textAlign: "center" }}>Set video URL</h3>
				<hr />
				<input
					type="text"
					style={{ width: "100%", margin: "0 auto" }}
					title="level video"
					className="form-control"
					value={videoUrl}
					onChange={(e) => setVideoUrl(e?.target?.value)}
				/>
			</Modal.Body>
			<Modal.Footer className="mx-auto modal-buttons">
				<button
					className="btn btn-primary__special"
					onClick={handleVideoUpdate}>
					Save
				</button>
				<button
					className="btn btn-primary"
					onClick={() => setCurrentModal(0)}>
					Close
				</button>
			</Modal.Footer>
		</span>
	);
};

const LevelFooter = ({ commentTabsRef }) => {
	const { currentUserData } = useContext<any>(UserContext);
	const { level } = useContext<any>(LevelContext);

	const [commentSort, setCommentSort] = useState<CommentSort>("priority");
	const [hasDoneQuery, setHasDoneQuery] = useState<number>(0);

	const observerRef = useRef<any>(null);

	const tabsToMap: any = [
		{
			text: "Most upvoted",
			tooltip: "Comments with the highest like/dislike score",
			value: "priority",
		},
		{
			text: "Newest",
			tooltip: "Newest comments first",
			value: "new",
		},
		{
			text: "Reviews",
			tooltip: "Only show comments highlighted as review",
			value: "reviewer",
		},
	];

	const mappedTabs = tabsToMap?.map((obj) => (
		<Fragment key={"tab-levelcomment-" + obj?.value}>
			<OverlayTrigger
				delay={{
					show: 250,
					hide: 250,
				}}
				placement="top"
				overlay={
					<Tooltip>
						<div>{obj?.tooltip}</div>
					</Tooltip>
				}>
				<button
					onClick={() => setCommentSort(obj?.value)}
					disabled={commentSort === obj?.value}
					className={commentSort === obj?.value ? "active" : ""}>
					{obj?.text}
				</button>
			</OverlayTrigger>
		</Fragment>
	));

	return (
		<>
			<div
				className="tab-container unselectable flex-tab no-blur"
				ref={commentTabsRef}
				style={{ marginTop: "20px" }}>
				{mappedTabs}
			</div>
			<div className="level-footer no-blur" ref={observerRef}>
				{!!currentUserData?._id && (
					<div className="card blur-base">
						<CommentBox
							setCommentSort={setCommentSort}
							setHasDoneQuery={setHasDoneQuery}
							commentTabsRef={commentTabsRef}
						/>
					</div>
				)}
				<LevelComments
					key={level?._id + "-level-comments"}
					observerRef={observerRef}
					commentSort={commentSort}
					setHasDoneQuery={setHasDoneQuery}
					hasDoneQuery={hasDoneQuery}
				/>
			</div>
		</>
	);
};

const CommentBox = ({ setCommentSort, setHasDoneQuery, commentTabsRef }) => {
	const { currentUserData } = useContext<any>(UserContext);
	const { level } = useContext<any>(LevelContext);

	const [lockCommentSubmit, setLockCommentSubmit] = useState<boolean>(false);
	const [commentBox, setCommentBox] = useState<string>("");
	const [commentHighlight, setCommentHighlight] = useState<boolean>(false);
	const [revealCommentBox, setRevealCommentBox] = useState<boolean>(false);
	const [previewState, setPreviewState] = useState<boolean>(false);

	const history = useHistory();
	const levelAuthor = level?.author;

	const userIsReviewer = currentUserData?.subRoles?.includes(
		UserConstants.SubRoles.REVIEWER,
	);
	const reviewerOrAdmin = userIsReviewer || currentUserData?.isAdmin;
	const reviewerOrStaff = userIsReviewer || currentUserData?.isStaff;
	const verified = currentUserData?.verified;
	const muted = currentUserData?.mainRoles?.includes(
		UserConstants.Punishments.MUTED,
	);
	const guarded = currentUserData?.subRoles?.includes(
		UserConstants.SubRoles.GUARDED,
	);
	const socialSetting = parseInt(levelAuthor?.social?.[0]) || 0;
	const socialDisable = socialSetting === 2;
	const friendDisable =
		socialSetting === 1 &&
		!currentUserData?.friends?.includes(levelAuthor?._id);
	const blockedDisable = levelAuthor?.blocked_users?.includes(
		currentUserData?._id,
	);
	const blockedDisable2 = currentUserData?.blocked_users?.includes(
		levelAuthor?._id,
	);
	const isLevelAuthor = levelAuthor?._id === currentUserData?._id;

	// get a permission based key to decide which message to pick
	const disableKey = useMemo(() => {
		if (guarded) return "guarded";
		if (muted) return "muted";
		if (isLevelAuthor) return -1;
		if (socialDisable && reviewerOrStaff) return "social-bypass";
		if (socialDisable) return "social";
		if (friendDisable && reviewerOrStaff) return "friends-bypass";
		if (friendDisable) return "friends";
		if (blockedDisable) return "blocked";
		if (blockedDisable2) return "blocked2";
		return -1;
	}, [
		guarded,
		muted,
		socialDisable,
		reviewerOrStaff,
		friendDisable,
		blockedDisable,
		blockedDisable2,
		isLevelAuthor,
	]);

	// messages for when to disable the comment box
	const disableMessages = {
		guarded: "Your account is guarded; commenting is disabled.",
		muted: "Commenting is disabled since your account is currently muted.",
		social: "The level author has disabled comments on this level.",
		friends: "Only friends of the level author can comment on this level.",
		blocked:
			"You're blocked by the level author and can't comment on their levels.",
		blocked2: "You cannot comment on the levels of users you've blocked.",
	};

	const permText = currentUserData?.isStaff
		? "staff session"
		: "reviewer subrole";
	const bypassMessages = {
		"social-bypass": `The level author has disabled comments on this level, but your ${permText} bypasses this restriction.`,
		"friends-bypass": `Only friends of the level author can comment on this level, but your ${permText} makes you seem extra friendly.`,
	};

	const handleChange = (e) => {
		const value = e.target.value;
		setCommentBox(value);
		// set a page store for level comment
		if (value !== "")
			return sessionStorage.setItem(`levelComment${level?._id}`, value);
		// remove it if empty
		sessionStorage.removeItem(`levelComment${level?._id}`);
	};

	// upon mount
	useEffect(() => {
		const value = sessionStorage.getItem(`levelComment${level?._id}`) || "";
		setCommentBox(value);
	}, [level?._id]);

	const handleCommentSubmit = (e) => {
		// prevent form from submitting as normal
		e.preventDefault();
		// return if empty
		if (commentBox === "")
			return toast.error("New comments can't be empty.");

		const msg = toast.loading("Sending out post dove...");
		// api call
		createLevelComment(level?._id, {
			content: removeExcessiveBreaks(commentBox),
			highlighted: commentHighlight,
		})
			.then(() => {
				// success
				setCommentBox("");
				setRevealCommentBox(false);
				setCommentHighlight(false);
				setPreviewState(false);
				setCommentSort("new");
				setHasDoneQuery(false);
				sessionStorage.removeItem(`levelComment${level?._id}`);
				window.scrollTo({
					top:
						commentTabsRef.current.getBoundingClientRect().top +
						window.scrollY -
						100,
					behavior: "smooth",
				});
			})
			.finally(() => {
				toast.dismiss(msg);
				setLockCommentSubmit(false);
			});
	};

	if (!verified)
		return (
			<div className="card-body center-textoutput">
				<h3>
					Make sure to{" "}
					<a href="/verify" onClick={(e) => pushHistory(history, e)}>
						verify
					</a>{" "}
					your account to get access to commenting.
				</h3>
			</div>
		);

	if (disableKey !== -1 && !disableKey.includes("bypass"))
		return (
			<div className="card-body center-textoutput">
				<h3>{disableMessages[disableKey]}</h3>
			</div>
		);

	if (!revealCommentBox)
		return (
			<>
				<div
					className={
						(bypassMessages?.[disableKey] ? "yellow" : "") +
						" card-header center-textoutput"
					}>
					{bypassMessages?.[disableKey] ||
						"Click the button to reveal the comment box"}
				</div>
				<div className="card-body d-flex justify-content-center">
					<button
						className="btn btn-primary__special"
						onClick={() => {
							setRevealCommentBox(true);
							requestAnimationFrame(() =>
								document
									.getElementById("revealed-comment-box")
									?.focus(),
							);
						}}>
						Reveal comment box
					</button>
				</div>
			</>
		);

	return (
		<>
			<div className="card-header">
				<h2>
					Add a comment to&nbsp;
					<i className="gray">{level?.name}</i>&nbsp;
					<span className="yellow">
						({commentBox?.length}
						/8000)
					</span>
				</h2>
			</div>
			<div className="card-body">
				{previewState ? (
					<div className="center-textoutput">
						<ReadMore
							content={commentBox}
							document={null}
							height={350}
							rank={currentUserData?.rank}
						/>
					</div>
				) : (
					<textarea
						id="revealed-comment-box"
						className="form-control"
						style={{
							resize: "none",
							marginLeft: "unset",
							width: "100%",
						}}
						maxLength={8000}
						placeholder="Leave a comment about what you liked and/or didn't like."
						value={commentBox}
						onChange={handleChange}
					/>
				)}
				<div
					style={{
						marginTop: "12px",
						display: "flex",
						gap: "8px",
						flexDirection: "row",
						alignItems: "center",
						flexWrap: "wrap",
					}}>
					<button
						disabled={lockCommentSubmit}
						onClick={handleCommentSubmit}
						className="btn btn-primary">
						Add comment
					</button>
					<button
						disabled={lockCommentSubmit}
						onClick={() => setRevealCommentBox(false)}
						className="btn btn-primary__danger">
						Hide comment box
					</button>
					<div style={{ display: "flex", gap: "6px" }}>
						<ToggleSwitch
							onToggle={() => setPreviewState((p) => !p)}
							isOn={previewState}
						/>
						<span>Preview</span>
					</div>
					{reviewerOrAdmin ? (
						<div style={{ display: "flex", gap: "6px" }}>
							<ToggleSwitch
								onToggle={() => setCommentHighlight((p) => !p)}
								isOn={commentHighlight}
							/>
							<span>Highlight as review</span>
						</div>
					) : null}
				</div>
			</div>
		</>
	);
};

const LevelComments = ({
	observerRef,
	commentSort,
	hasDoneQuery,
	setHasDoneQuery,
}) => {
	const { level } = useContext(LevelContext);
	const { setThreadProps } = useContext(GlobalContext);

	const history = useHistory();

	const [openThread, setOpenThread] = useState<any>(false);
	const [refererComment, setRefererComment] = useState<any[]>([]);

	const [levelComments, setLevelComments] = useState<any[]>([]);
	const [commentsChanged, setCommentsChanged] = useState<boolean>(false);
	const [isQuerying, setIsQuerying] = useState<boolean>(false);
	const [animationState, setAnimationState] = useState<
		"idle" | "sliding-out" | "positioning" | "sliding-in"
	>("idle");
	const [previousPage, setPreviousPage] = useState<any>(null);
	const [page, setPage] = useState<number>(1);
	const [direction, setDirection] = useState<number>(1);
	const [numberOfPages, setNumberOfPages] = useState<number>(1);
	const [message, setMessage] = useState<string>("");

	const abortRef = useRef<AbortController | null>(null);

	useEffect(() => {
		const query = new URLSearchParams(window.location.search);
		const referer = query.get("referer");
		if (referer && !refererComment.length) {
			getLevelComment(referer).then((res) => {
				if (res?.data) {
					setRefererComment([res?.data?.comment]);
					setOpenThread(true);
				}
			});
		}
	}, [refererComment?.length]);

	useEffect(() => {
		if (openThread && refererComment?.length) {
			setThreadProps({
				document: refererComment[0],
				setDocuments: setRefererComment,
				origin: "level",
			});
			history.replace({
				pathname: window.location.pathname,
				search: "",
			});
		}
	}, [openThread, refererComment, history, setThreadProps]);

	useEffect(() => {
		setCommentsChanged(true);
		setPreviousPage(-1);
		setDirection(1);
		setPage(1);
		setAnimationState("idle");
	}, [commentSort]);

	useEffect(() => {
		// handle page change
		if (page === previousPage) return;
		if (page > previousPage) setDirection(-1);
		else if (page < previousPage) setDirection(1);
		setCommentsChanged(true);
	}, [page, previousPage]);

	const handleTransitionEnd = useCallback(
		(animStateToUse) => {
			if (animStateToUse === "sliding-out") {
				if (page === previousPage && hasDoneQuery)
					return setCommentsChanged(false);

				// Cancel any previous request
				if (abortRef.current) abortRef.current.abort();

				// Create a new controller
				const controller = new AbortController();
				abortRef.current = controller;

				// Content has slid out, now fetch new data
				getLevelCommentsPage(
					level?._id,
					{ page, sort: commentSort },
					controller.signal,
				)
					.then((response) => {
						const data = response?.data;
						if (data) {
							setPreviousPage(page);
							setLevelComments(data?.levelComments || []);
							setNumberOfPages(data?.numberOfPages || 1);
							setMessage(data?.message || "");
						}
					})
					.catch(() => {
						setLevelComments([]);
						setNumberOfPages(0);
					})
					.finally(() => {
						setCommentsChanged(false);
						setHasDoneQuery(2);

						// Position off-screen right without transition
						setAnimationState("positioning");

						// Then start slide-in animation
						requestAnimationFrame(() => {
							setIsQuerying(false);
							setAnimationState("sliding-in");
						});
					});
			} else if (animStateToUse === "sliding-in") {
				// Animation complete, return to idle state
				setAnimationState("idle");
			}
		},
		[
			page,
			previousPage,
			level?._id,
			commentSort,
			hasDoneQuery,
			setHasDoneQuery,
		],
	);

	useEffect(() => {
		const observer = new IntersectionObserver((entries) => {
			if (
				entries[0].isIntersecting &&
				((commentsChanged &&
					!isQuerying &&
					animationState === "idle") ||
					!hasDoneQuery)
			) {
				// Start the slide-out animation
				setAnimationState("sliding-out");
				setIsQuerying(true);

				if (!hasDoneQuery || !levelComments?.length) {
					setHasDoneQuery(1);
					handleTransitionEnd("sliding-out");
				}
			}
		});

		if (observerRef.current) {
			observer.observe(observerRef.current);
		}

		return () => {
			observer.disconnect();
		};
	}, [
		commentsChanged,
		observerRef,
		commentSort,
		page,
		level?._id,
		isQuerying,
		animationState,
		hasDoneQuery,
		levelComments?.length,
		handleTransitionEnd,
		setHasDoneQuery,
	]);

	const getTransformValue = () => {
		switch (animationState) {
			case "sliding-out":
				return `${-150 * direction}%`; // Slide left off screen
			case "positioning":
				return `${150 * direction}%`; // Position off-screen right (no transition)
			case "sliding-in":
				return "0%"; // Slide in from right to center
			case "idle":
			default:
				return "0%"; // Normal position
		}
	};

	const getTransition = () => {
		switch (animationState) {
			case "positioning":
				return "none"; // No transition when positioning
			case "idle":
				return "none"; // No transition when idle
			default:
				return "transform 0.6s ease-in-out"; // Smooth transition for sliding
		}
	};

	const style = {
		"--transform-value": getTransformValue(),
		transition: getTransition(),
	} as React.CSSProperties;

	if (hasDoneQuery !== 2 || (!levelComments?.length && isQuerying)) {
		return (
			<div className="d-flex justify-content-center">
				<Spinner animation="grow" variant="primary" />
			</div>
		);
	}

	if (levelComments?.length === 0) {
		return (
			<div className="card card-body center-textoutput blur-force">
				<h3>{message || "No comments found"}</h3>
			</div>
		);
	}

	return (
		<>
			<Paginate
				numberOfPages={numberOfPages || 1}
				type="noparams"
				noparampage={page}
				setPage={setPage}
			/>
			<div
				className="blur-base level-comments"
				style={style}
				onTransitionEnd={() => handleTransitionEnd(animationState)}>
				{levelComments?.map((comment) => (
					<Comment
						key={comment?._id}
						comment={comment}
						setLevelComments={setLevelComments}
					/>
				))}
			</div>
		</>
	);
};

const Comment = ({ comment, setLevelComments }) => {
	const { level } = useContext<any>(LevelContext);

	const [editContent, setEditContent] = useState(comment?.content);

	const commentAuthor = comment?.author;
	const deleted = comment?.visibility === "deleted";
	const reviewer: boolean = commentAuthor?.subRoles?.includes(
		UserConstants.SubRoles.REVIEWER,
	);
	const highlighted: boolean = !!comment?.highlighted;
	const betaComment = comment?.betaReview;
	const pinned = comment?.pinned;

	const visibilityValues = {
		public: {
			icon: pinned ? "push_pin" : "public",
			tooltip:
				(pinned ? "[PINNED] " : "") +
				(betaComment
					? "This comment was posted while the level was in beta"
					: "This comment is public"),
		},
		private: {
			icon: "lock",
			tooltip: betaComment
				? "Level is private with all of its comments. This comment was posted while the level was in beta"
				: "Since the level is currently private, this comment is hidden from others",
		},
		deleted: {
			icon: "delete",
			tooltip: "This comment has been deleted",
		},
	};

	const visibilityKey = useMemo(() => {
		if (deleted) return "deleted";
		if (level?.status === LevelConstants.Statuses.PRIVATE) return "private";
		return "public";
	}, [deleted, level?.status]);

	const commentVisibilityButton = (
		<div>
			<OverlayTrigger
				placement="right"
				overlay={
					<Tooltip>{visibilityValues[visibilityKey].tooltip}</Tooltip>
				}>
				<span
					className={`material-icons-outlined translate ${betaComment && "yellow"}`}>
					{visibilityValues[visibilityKey].icon}
				</span>
			</OverlayTrigger>
		</div>
	);

	const reviewerDisplay = (
		<OverlayTrigger
			placement="left"
			overlay={<Tooltip>This user is a reviewer</Tooltip>}>
			<div
				className="flex-center"
				style={{
					padding: "0 8px",
					border: "1px solid rgba(255, 217, 0, 1)",
					backgroundColor: "rgba(255, 217, 0, 0.1)",
					borderRadius: "12px",
					gap: "4px",
				}}>
				<span className="material-icons-outlined">reviews</span>
				Reviewer
			</div>
		</OverlayTrigger>
	);

	const style: CSSProperties = {
		...(highlighted && {
			boxShadow: "0 0 30px 5px rgba(255, 229, 81, 0.4)",
			border: "2px solid rgba(255, 217, 0, 1)",
		}),
		...(deleted && {
			opacity: 0.5,
			backgroundColor: "rgba(240, 60, 60, 0.3)",
			border: "2px solid rgba(255, 38, 0, 1)",
		}),
	};

	return (
		<div
			className="card center-textoutput"
			style={style}
			id={`levelcomment-${comment?._id}`}>
			<div
				className="card-header"
				style={{
					display: "flex",
					justifyContent: "space-between",
					padding: "8px",
				}}>
				<UsernameAvatarDisplay user={commentAuthor} />
				{reviewer && highlighted && <>{reviewerDisplay}</>}
			</div>
			<div className="card-body">
				<div className="display-button-container">
					<CommentButtons
						comment={comment}
						origin="level"
						type="comment"
						elementsToDisplay={[
							"thread",
							"copy",
							"edit",
							"pin",
							"highlight",
							"delete",
							"obliterate",
						]}
						setComments={setLevelComments}
						elementProps={{
							editContent,
							parentAuthor: level?.author?._id,
						}}
						thread={false}
					/>
					{commentVisibilityButton}
				</div>
				{comment?.editState && !comment?.previewState ? (
					<textarea
						className="form-control"
						style={{ width: "100%", margin: "unset" }}
						value={editContent}
						onChange={(e) => setEditContent(e.target.value)}
					/>
				) : (
					<ReadMore
						content={
							comment?.previewState
								? editContent
								: comment?.content
						}
						document={comment}
						height={350}
						rank={comment?.author?.rank}
					/>
				)}
				<hr />
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						width: "100%",
						height: "max-content",
						gap: "4px",
						overflowWrap: "anywhere",
						whiteSpace: "pre-wrap",
					}}>
					<CommentReaction
						document={comment}
						setDocuments={setLevelComments}
						type="level"
					/>
					<div>
						Posted on{" "}
						<b>
							{moment(comment?.postDate).format(
								`MMM D, YYYY - h:mm A`,
							)}
						</b>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Level;
